import { useEffect, useState, useCallback } from 'react';
import { Analytics, getScreenFromUrl } from '@trello/atlassian-analytics';
import { waitForQuickloadUrl, getPreloadsFromPath } from '@trello/quickload';
import type { QuickloadResponse } from '@trello/quickload';
import { hydrateApolloCacheFromQuickloadResults } from './hydrateApolloCacheFromQuickloadResults';
import { cachedFlagValue } from './cachedFlagValue';
import { sendErrorEvent } from '@trello/error-reporting';

const getQuickLoadResults = async (): Promise<QuickloadResponse[]> => {
  const { preloads } = getPreloadsFromPath();
  return Promise.all(preloads.map(({ url }) => waitForQuickloadUrl(url)));
};

export const useQuickload = () => {
  const [isQuickLoading, setIsQuickLoading] = useState(true);

  const syncQuickLoadData = useCallback(async () => {
    const startTime = new Date().getTime();

    try {
      const results = await getQuickLoadResults();
      hydrateApolloCacheFromQuickloadResults(results);

      Analytics.sendOperationalEvent({
        action: 'succeeded',
        actionSubject: 'quickload',
        attributes: {
          waitTime: Date.now() - startTime,
        },
        source: getScreenFromUrl(),
      });
    } catch (err) {
      sendErrorEvent(err);
      Analytics.sendOperationalEvent({
        action: 'failed',
        actionSubject: 'quickload',
        attributes: {
          waitTime: Date.now() - startTime,
        },
        source: getScreenFromUrl(),
      });
    } finally {
      setIsQuickLoading(false);
    }
  }, [setIsQuickLoading]);

  useEffect(() => {
    if (cachedFlagValue('fep.sync-quickload-to-apollo', false)) {
      syncQuickLoadData();
    } else {
      setIsQuickLoading(false);
    }
  }, [setIsQuickLoading, syncQuickLoadData]);

  return isQuickLoading;
};
